import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiService } from '../services/api'
import config from '../config/config.js'
import apiCallManager from '../utils/apiCallManager'

export const useNutritionStore = create(
  persist(
    (set, get) => ({
      // State
      programs: [],
      selectedProgram: null,
      selectedDate: new Date(),
      nutritionData: null,
      clientDetails: null,
      loggedFoods: {}, // Track logged foods by food ID
      foodNotes: {}, // Track notes by food ID
      foodQuantities: {}, // Track custom quantities by food ID
      foodLogPayloads: {},
      isLoading: false,
      error: null,
      
      // Legacy state for backward compatibility
      meals: [
        // Sample meals for demo
        {
          id: 'demo-1',
          name: 'Healthy Breakfast',
          type: 'breakfast',
          date: new Date().toISOString().split('T')[0],
          foods: [
            { id: '6', name: 'Oats', calories: 150, protein: 6, carbs: 25, fat: 3, quantity: 0.4 },
            { id: '8', name: 'Banana', calories: 89, protein: 1.1, carbs: 23, fat: 0.3, quantity: 1 },
            { id: '7', name: 'Greek Yogurt', calories: 118, protein: 20, carbs: 7, fat: 0.8, quantity: 2 }
          ],
          createdAt: new Date().toISOString()
        },
        {
          id: 'demo-2',
          name: 'Power Lunch',
          type: 'lunch',
          date: new Date().toISOString().split('T')[0],
          foods: [
            { id: '1', name: 'Chicken Breast', calories: 165, protein: 31, carbs: 0, fat: 3.6, quantity: 1.5 },
            { id: '2', name: 'Brown Rice', calories: 111, protein: 2.6, carbs: 23, fat: 0.9, quantity: 1 },
            { id: '3', name: 'Broccoli', calories: 34, protein: 2.8, carbs: 7, fat: 0.4, quantity: 1.5 }
          ],
          createdAt: new Date().toISOString()
        }
      ],
      dailyGoals: {
        calories: 2000,
        protein: 150, // grams
        carbs: 250,   // grams
        fat: 67,      // grams
        fiber: 25,    // grams
        water: 8      // glasses
      },
      foodDatabase: [
        // Sample food items
        { id: '1', name: 'Chicken Breast', calories: 165, protein: 31, carbs: 0, fat: 3.6, fiber: 0, serving: '100g' },
        { id: '2', name: 'Brown Rice', calories: 111, protein: 2.6, carbs: 23, fat: 0.9, fiber: 1.8, serving: '100g' },
        { id: '3', name: 'Broccoli', calories: 34, protein: 2.8, carbs: 7, fat: 0.4, fiber: 2.6, serving: '100g' },
        { id: '4', name: 'Salmon', calories: 208, protein: 20, carbs: 0, fat: 13, fiber: 0, serving: '100g' },
        { id: '5', name: 'Avocado', calories: 160, protein: 2, carbs: 9, fat: 15, fiber: 7, serving: '100g' },
        { id: '6', name: 'Oats', calories: 389, protein: 17, carbs: 66, fat: 7, fiber: 11, serving: '100g' },
        { id: '7', name: 'Greek Yogurt', calories: 59, protein: 10, carbs: 3.6, fat: 0.4, fiber: 0, serving: '100g' },
        { id: '8', name: 'Banana', calories: 89, protein: 1.1, carbs: 23, fat: 0.3, fiber: 2.6, serving: '100g' },
      ],
      waterIntake: 4, // Demo: 4 glasses consumed
      isLoading: false,
      error: null,

      // Actions
      addMeal: (meal) => {
        const newMeal = {
          id: Date.now().toString(),
          ...meal,
          date: meal.date || new Date().toISOString().split('T')[0],
          createdAt: new Date().toISOString()
        }
        
        set(state => ({
          meals: [...state.meals, newMeal]
        }))
      },

      updateMeal: (id, updates) => {
        set(state => ({
          meals: state.meals.map(meal =>
            meal.id === id ? { ...meal, ...updates } : meal
          )
        }))
      },

      deleteMeal: (id) => {
        set(state => ({
          meals: state.meals.filter(meal => meal.id !== id)
        }))
      },

      addFoodToMeal: (mealId, foodItem) => {
        set(state => ({
          meals: state.meals.map(meal =>
            meal.id === mealId 
              ? { 
                  ...meal, 
                  foods: [...(meal.foods || []), {
                    ...foodItem,
                    id: Date.now().toString(),
                    addedAt: new Date().toISOString()
                  }]
                }
              : meal
          )
        }))
      },

      removeFoodFromMeal: (mealId, foodId) => {
        set(state => ({
          meals: state.meals.map(meal =>
            meal.id === mealId 
              ? { 
                  ...meal, 
                  foods: meal.foods?.filter(food => food.id !== foodId) || []
                }
              : meal
          )
        }))
      },

      // Daily Goals
      updateDailyGoals: (goals) => {
        set(state => ({
          dailyGoals: { ...state.dailyGoals, ...goals }
        }))
      },

      // Water Tracking
      addWaterIntake: (glasses = 1) => {
        const today = new Date().toISOString().split('T')[0]
        set(state => ({
          waterIntake: state.waterIntake + glasses
        }))
      },

      resetDailyWater: () => {
        set({ waterIntake: 0 })
      },

      // Food Database
      addCustomFood: (food) => {
        const newFood = {
          id: Date.now().toString(),
          ...food,
          isCustom: true,
          createdAt: new Date().toISOString()
        }
        
        set(state => ({
          foodDatabase: [...state.foodDatabase, newFood]
        }))
      },

      searchFood: (query) => {
        const { foodDatabase } = get()
        return foodDatabase.filter(food =>
          food.name.toLowerCase().includes(query.toLowerCase())
        )
      },

      // Statistics and Analytics
      getDailyNutrition: (date = null) => {
        const targetDate = date || new Date().toISOString().split('T')[0]
        const { meals } = get()
        
        const dayMeals = meals.filter(meal => meal.date === targetDate)
        
        const totals = dayMeals.reduce((acc, meal) => {
          meal.foods?.forEach(food => {
            const multiplier = food.quantity || 1
            acc.calories += (food.calories || 0) * multiplier
            acc.protein += (food.protein || 0) * multiplier
            acc.carbs += (food.carbs || 0) * multiplier
            acc.fat += (food.fat || 0) * multiplier
            acc.fiber += (food.fiber || 0) * multiplier
          })
          return acc
        }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 })

        return totals
      },

      getWeeklyNutritionTrend: () => {
        const { meals } = get()
        const weekData = []
        
        for (let i = 6; i >= 0; i--) {
          const date = new Date()
          date.setDate(date.getDate() - i)
          const dateString = date.toISOString().split('T')[0]
          
          const dayNutrition = get().getDailyNutrition(dateString)
          weekData.push({
            date: dateString,
            ...dayNutrition
          })
        }
        
        return weekData
      },

      getNutritionProgress: (date = null) => {
        const dailyNutrition = get().getDailyNutrition(date)
        const { dailyGoals } = get()
        
        return {
          calories: {
            current: dailyNutrition.calories,
            goal: dailyGoals.calories,
            percentage: Math.min((dailyNutrition.calories / dailyGoals.calories) * 100, 100)
          },
          protein: {
            current: dailyNutrition.protein,
            goal: dailyGoals.protein,
            percentage: Math.min((dailyNutrition.protein / dailyGoals.protein) * 100, 100)
          },
          carbs: {
            current: dailyNutrition.carbs,
            goal: dailyGoals.carbs,
            percentage: Math.min((dailyNutrition.carbs / dailyGoals.carbs) * 100, 100)
          },
          fat: {
            current: dailyNutrition.fat,
            goal: dailyGoals.fat,
            percentage: Math.min((dailyNutrition.fat / dailyGoals.fat) * 100, 100)
          },
          water: {
            current: get().waterIntake,
            goal: dailyGoals.water,
            percentage: Math.min((get().waterIntake / dailyGoals.water) * 100, 100)
          }
        }
      },

      clearError: () => {
        set({ error: null })
      },

      // Reset user-scoped nutrition state when auth user changes.
      resetUserScopedData: () => {
        set({
          programs: [],
          selectedProgram: null,
          selectedDate: new Date(),
          nutritionData: null,
          clientDetails: null,
          loggedFoods: {},
          foodNotes: {},
          foodQuantities: {},
          foodLogPayloads: {},
          isLoading: false,
          error: null,
          // Legacy state (also persisted to localStorage — must be cleared too)
          meals: [],
          dailyGoals: { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, water: 0 },
          foodDatabase: [],
          waterIntake: 0
        })
      },

      // New API Methods
      fetchClientPrograms: async (companyUrl) => {
        const requestKey = `nutrition:fetchClientPrograms:${companyUrl}`
        
        try {
          const result = await apiCallManager.executeRequest(
            requestKey,
            async (signal) => {
              return await apiService.post(`/${companyUrl}/getclientprograms`, {}, signal)
            },
            {
              onStart: () => set({ isLoading: true, error: null }),
              onComplete: (response) => {
                if (response.success) {
                  const { programs, clientDetais } = response.data
                  set({ 
                    programs,
                    clientDetails: clientDetais,
                    selectedProgram: programs.find(p => p.id === clientDetais.activeprogram_id) || programs[0],
                    isLoading: false 
                  })
                } else {
                  set({ error: response.error, isLoading: false })
                }
              },
              onError: (error) => {
                if (error.message !== 'Request cancelled') {
                  set({ error: error.message, isLoading: false })
                }
              }
            }
          )
          return result
        } catch (error) {
          if (error.message !== 'Request cancelled') {
            set({ error: error.message, isLoading: false })
          }
        }
      },

      fetchNutritionData: async (companyUrl, date, programId = '') => {
        const logdate = formatDateForAPI(date)
        const requestKey = `fetchNutritionData:${companyUrl}:${logdate}:${programId}`
        
        try {
          const result = await apiCallManager.executeRequest(
            requestKey,
            async (signal) => {
              return await apiService.post(`/${companyUrl}/clientnutrition`, {
                logdate,
                program_id: ''
              }, signal)
            },
            {
              onStart: () => set({ isLoading: true, error: null }),
              onComplete: (response) => {
                if (response.success) {
                  const { users, programs } = response.data
                  set({ 
                    nutritionData: programs,
                    clientDetails: users,
                    isLoading: false 
                  })
                } else {
                  set({ error: response.error, isLoading: false })
                }
              },
              onError: (error) => {
                if (error.message !== 'Request cancelled') {
                  set({ error: error.message, isLoading: false })
                }
              }
            }
          )
          return result
        } catch (error) {
          if (error.message !== 'Request cancelled') {
            set({ error: error.message, isLoading: false })
          }
        }
      },

      // Food logging methods
      toggleFoodLogged: (foodId) => {
        set(state => ({
          loggedFoods: {
            ...state.loggedFoods,
            [foodId]: !state.loggedFoods[foodId]
          }
        }))
      },

      setFoodLogged: (foodId, value) => {
        set(state => ({
          loggedFoods: {
            ...state.loggedFoods,
            [foodId]: value
          }
        }))
      },
      logSingleFood: async (companyUrl, apiFoodId, stateFoodId, payload) => {
        const normalized = {
          log_qty: Number(payload?.log_qty) || 0,
          clientnote: payload?.clientnote || '',
          toggle: payload?.toggle ? 1 : 0
        }

        const { foodLogPayloads } = get()
        const lastPayload = foodLogPayloads[stateFoodId]
        const isDuplicate = lastPayload &&
          lastPayload.log_qty === normalized.log_qty &&
          lastPayload.clientnote === normalized.clientnote &&
          lastPayload.toggle === normalized.toggle

        if (isDuplicate) {
          return { success: true, skipped: true }
        }

        const isUpdate = !!payload?.lognutritionsinglefoods_id
        const requestKey = isUpdate
          ? `logSingleFood:update:${companyUrl}:${payload.lognutritionsinglefoods_id || apiFoodId}:${normalized.toggle}`
          : `logSingleFood:create:${companyUrl}:${payload?.mealfood_id || stateFoodId}:${normalized.toggle}`

        try {
          const result = await apiCallManager.executeRequest(
            requestKey,
            async (signal) => {
              try {
                const { selectedProgram, selectedDate } = get()
                const logdate = formatDateForAPI(selectedDate)

                const endpoint = isUpdate
                  ? `/${companyUrl}${config.api.endpoints.updateLogNutritionFood}`
                  : `/${companyUrl}${config.api.endpoints.createLogNutritionFood}`

                const body = isUpdate
                  ? [
                      {
                        lognutritionsinglefoods_id: payload?.lognutritionsinglefoods_id || apiFoodId,
                        data: normalized
                      }
                    ]
                  : {
                      meal_id: payload?.meal_id || null,
                      mealplan_id: payload?.mealplan_id || null,
                      log_qty: normalized.log_qty,
                      isnutritionfood: false,
                      program_id: payload?.program_id || selectedProgram?.id || null,
                      mealfoods: [
                        {
                          id: payload?.mealfood_id || stateFoodId,
                          log_qty: normalized.log_qty,
                          clientnote: normalized.clientnote
                        }
                      ],
                      logdate,
                      toggle: normalized.toggle
                    }

                const response = await apiService.post(endpoint, body, signal)
                return response
              } catch (error) {
                if (error.name === 'AbortError') throw error
                return { success: false, error: error.message }
              }
            },
            {
              onStart: () => set({ error: null }),
              onComplete: (response) => {
                if (response.success) {
                  set(state => ({
                    loggedFoods: {
                      ...state.loggedFoods,
                      [stateFoodId]: normalized.toggle === 1
                    },
                    foodLogPayloads: {
                      ...state.foodLogPayloads,
                      [stateFoodId]: normalized
                    }
                  }))
                } else {
                  set({ error: response.error })
                }
              },
              onError: (error) => {
                if (error.message !== 'Request cancelled') {
                  set({ error: error.message })
                }
              }
            }
          )

          return result
        } catch (error) {
          if (error.message === 'Request cancelled') {
            return { success: false, error: 'Request cancelled' }
          }
          return { success: false, error: error.message }
        }
      },

      updateFoodNote: (foodId, note) => {
        set(state => ({
          foodNotes: {
            ...state.foodNotes,
            [foodId]: note
          }
        }))
      },

      updateFoodQuantity: (foodId, quantity) => {
        set(state => ({
          foodQuantities: {
            ...state.foodQuantities,
            [foodId]: quantity
          }
        }))
      },

      setSelectedProgram: (program) => {
        set({ selectedProgram: program })
      },

      setSelectedDate: (date) => {
        set({ selectedDate: date })
      },

      // Get nutrition totals for logged foods
      getLoggedNutritionTotals: () => {
        const { nutritionData, loggedFoods, foodQuantities } = get()
        if (!nutritionData?.mealplans) return { calories: 0, protein: 0, carbs: 0, fat: 0 }

        let totals = { calories: 0, protein: 0, carbs: 0, fat: 0 }

        nutritionData.mealplans.forEach(mealplan => {
          mealplan.meals.forEach(meal => {
            meal.foods.forEach(food => {
              if (loggedFoods[food.mealfood_id]) {
                // food.calories is already the total for food.qty (not per-unit).
                // Derive per-unit value then scale by the actually logged quantity.
                const planQty = food.qty || 1
                const loggedQty = foodQuantities[food.mealfood_id] ?? food.log_qty ?? food.qty ?? 1
                const scale = loggedQty / planQty
                totals.calories += (food.calories || 0) * scale
                totals.protein += (food.protein || 0) * scale
                totals.carbs += (food.carbs || 0) * scale
                totals.fat += (food.fat || 0) * scale
              }
            })
          })
        })

        return totals
      },

      // Get scheduled nutrition totals
      getScheduledNutritionTotals: () => {
        const { nutritionData } = get()
        if (!nutritionData?.mealplans) return { calories: 0, protein: 0, carbs: 0, fat: 0 }

        let totals = { calories: 0, protein: 0, carbs: 0, fat: 0 }

        nutritionData.mealplans.forEach(mealplan => {
          mealplan.meals.forEach(meal => {
            meal.foods.forEach(food => {
              // food.calories is already the total for the plan qty — add directly, no multiplication
              totals.calories += (food.calories || 0)
              totals.protein += (food.protein || 0)
              totals.carbs += (food.carbs || 0)
              totals.fat += (food.fat || 0)
            })
          })
        })

        return totals
      }
    }),
    {
      name: 'nutrition-storage',
      partialize: (state) => ({
        // Legacy state
        meals: state.meals,
        dailyGoals: state.dailyGoals,
        foodDatabase: state.foodDatabase,
        waterIntake: state.waterIntake,
        // New state
        programs: state.programs,
        selectedProgram: state.selectedProgram,
        selectedDate: state.selectedDate?.toISOString ? state.selectedDate.toISOString() : state.selectedDate,
        loggedFoods: state.loggedFoods,
        foodNotes: state.foodNotes,
        foodQuantities: state.foodQuantities,
        clientDetails: state.clientDetails
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.selectedDate && typeof state.selectedDate === 'string') {
          state.selectedDate = new Date(state.selectedDate)
        }
      }
    }
  )
)

// Helper function to format date as YYYY-M-D
const formatDateForAPI = (date) => {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = d.getMonth() + 1 // getMonth() returns 0-11
  const day = d.getDate()
  return `${year}-${month}-${day}`
}
